/**
 * Audit Log Service
 *
 * API integration for Admin Audit Log Viewer.
 * Supports filtering, sorting, pagination, and export.
 *
 * CRITICAL: Uses API field names (camelCase), NOT database columns (rp_*)
 */

import { tableService } from './tableService'
import { apiService } from './apiService'
import type {
  AuditLog,
  AuditLogFilters,
} from '@/types/auditLog.types'
import type { ApiResponse, SearchOptions } from '@/types/api.types'

/**
 * Convert UI filters to backend filter DSL
 */
function buildFilters(filters: AuditLogFilters): SearchOptions['filters'] {
  const result: SearchOptions['filters'] = []

  // Date range filter (ISO 8601 format)
  if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
    result.push({
      key: 'createdAt',
      constraint_type: 'GE',
      value: filters.dateRange[0],
    })
    result.push({
      key: 'createdAt',
      constraint_type: 'LE',
      value: filters.dateRange[1],
    })
  }

  // Event types (multiple selection)
  if (filters.eventTypes && filters.eventTypes.length > 0) {
    if (filters.eventTypes.length === 1) {
      result.push({
        key: 'eventType',
        constraint_type: 'EQUAL',
        value: filters.eventTypes[0],
      })
    } else {
      result.push({
        key: 'eventType',
        constraint_type: 'IN',
        values: filters.eventTypes,
      })
    }
  }

  // User ID filter - requires valid UUID format (backend UUID columns don't support partial matching)
  if (filters.userId && filters.userId.trim()) {
    const value = filters.userId.trim()
    // Only send if it looks like a valid UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (uuidRegex.test(value)) {
      result.push({
        key: 'userId',
        constraint_type: 'EQUAL',
        value: value,
      })
    }
  }

  // User Name filter - text search with CONTAINS
  if (filters.userName && filters.userName.trim()) {
    result.push({
      key: 'userName',
      constraint_type: 'CONTAINS',
      value: filters.userName.trim(),
    })
  }

  // Organization ID filter - requires valid UUID format (backend UUID columns don't support partial matching)
  if (filters.orgId && filters.orgId.trim()) {
    const value = filters.orgId.trim()
    // Only send if it looks like a valid UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (uuidRegex.test(value)) {
      result.push({
        key: 'orgId',
        constraint_type: 'EQUAL',
        value: value,
      })
    }
  }

  // Organization Name filter - text search with CONTAINS
  if (filters.orgName && filters.orgName.trim()) {
    result.push({
      key: 'orgName',
      constraint_type: 'CONTAINS',
      value: filters.orgName.trim(),
    })
  }

  // Source filter
  if (filters.source) {
    result.push({
      key: 'source',
      constraint_type: 'EQUAL',
      value: filters.source,
    })
  }

  // IP Address filter
  if (filters.ipAddress) {
    result.push({
      key: 'ipAddress',
      constraint_type: 'CONTAINS',
      value: filters.ipAddress,
    })
  }

  return result
}

/**
 * Fetch audit logs with filters, sorting, and pagination
 */
export async function fetchAuditLogs(
  filters: AuditLogFilters,
  pagination: { page: number; pageSize: number },
  sort: { field: string; direction: 'asc' | 'desc' }
): Promise<ApiResponse<{ events: AuditLog[]; totalCount: number }>> {
  const searchOptions: SearchOptions = {
    filters: buildFilters(filters),
    order_by: [{ key: sort.field, direction: sort.direction }],
    limit: pagination.pageSize,
    offset: (pagination.page - 1) * pagination.pageSize,
    includeDeleted: false, // Don't show soft-deleted audit logs by default
  }

  // Use tableService for standard /search endpoint handling
  const response = await tableService.search<AuditLog>('/audit_logs', searchOptions)

  if (!response.success || !response.data) {
    return response as any
  }

  // Transform to expected format with totalCount
  return {
    success: true,
    data: {
      events: response.data,
      totalCount: response.meta?.total || 0,
    },
    meta: response.meta,
  }
}

/**
 * Fetch distinct event types from backend
 * Returns all unique eventType values that exist in the audit logs
 */
export async function fetchEventTypes(): Promise<ApiResponse<string[]>> {
  return apiService.get<string[]>('/audit_logs/distinct/eventType')
}

/**
 * Fetch distinct sources from backend
 * Returns all unique source values that exist in the audit logs
 */
export async function fetchSources(): Promise<ApiResponse<string[]>> {
  return apiService.get<string[]>('/audit_logs/distinct/source')
}

/**
 * Search users by email/name for autocomplete
 */
export async function searchUsers(_query: string): Promise<ApiResponse<Array<{ uid: string; email: string; name: string }>>> {
  // TODO: Implement user search endpoint
  // For now, return empty results
  return Promise.resolve({
    success: true,
    data: [],
  })
}

/**
 * Search organizations by name for autocomplete
 */
export async function searchOrganizations(_query: string): Promise<ApiResponse<Array<{ uid: string; name: string }>>> {
  // TODO: Implement organization search endpoint
  // For now, return empty results
  return Promise.resolve({
    success: true,
    data: [],
  })
}

/**
 * Export audit logs to CSV format
 */
export function exportToCSV(logs: AuditLog[]): string {
  if (logs.length === 0) return ''

  // CSV headers
  const headers = [
    'Timestamp',
    'Event Type',
    'User ID',
    'Organization ID',
    'Source',
    'IP Address',
    'Event Details',
    'Metadata',
  ]

  // Convert logs to CSV rows
  const rows = logs.map(log => [
    log.createdAt,
    log.eventType,
    log.userId || '',
    log.orgId || '',
    log.source,
    log.ipAddress || '',
    JSON.stringify(log.eventDetails),
    JSON.stringify(log.metadata),
  ])

  // Build CSV string
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
  ].join('\n')

  return csvContent
}

/**
 * Export audit logs to JSON format
 */
export function exportToJSON(logs: AuditLog[]): string {
  return JSON.stringify(logs, null, 2)
}

/**
 * Export audit logs to NDJSON (newline-delimited JSON) format
 */
export function exportToNDJSON(logs: AuditLog[]): string {
  return logs.map(log => JSON.stringify(log)).join('\n')
}

/**
 * Download exported data as a file
 */
export function downloadExport(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const auditLogService = {
  fetchAuditLogs,
  fetchEventTypes,
  fetchSources,
  searchUsers,
  searchOrganizations,
  exportToCSV,
  exportToJSON,
  exportToNDJSON,
  downloadExport,
}
